/**
 * Upscale-Jobs (Collection `upscaleJobs`, geschrieben vom Worker in
 * firebase_aq/functions-upscaler): Live-Zugriff + kleine Anzeige-Helfer.
 *
 * Ein Job-Dokument: status (queued|running|done|failed), step, steps[],
 * stages{upscale, final, upscaleLayer, finalOpaque, twin, …}, report{analysis,
 * detail, cutout}, taskScore{min, avg, limitedBy}, taskScoreLimit, passed,
 * costUsd, ms, thumb, source ('order'|'lab'|'test'), taskUid.
 */
import { collection, doc, limit, onSnapshot, orderBy, query, Timestamp, where } from "firebase/firestore";
import { db } from "../fb";

const COLLECTION = "upscaleJobs";

/** Ein Job live — liefert die Unsubscribe-Funktion */
export function subscribeUpscaleJob(jobId, onJob, onError) {
  return onSnapshot(
    doc(db, COLLECTION, jobId),
    (snap) => onJob(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => (onError ? onError(err) : console.warn("upscaleJob:", err.message))
  );
}

/** Jobs der letzten Stunden live, neueste zuerst */
export function subscribeRecentUpscaleJobs({ hours = 24, max = 150 } = {}, onJobs, onError) {
  const since = Timestamp.fromMillis(Date.now() - hours * 3600 * 1000);
  const q = query(collection(db, COLLECTION), where("createdAt", ">=", since), orderBy("createdAt", "desc"), limit(max));
  return onSnapshot(
    q,
    (snap) => onJobs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => (onError ? onError(err) : console.warn("upscaleJobs:", err.message))
  );
}

export const isJobRunning = (job) => !!job && (job.status === "queued" || job.status === "running");

export const jobStatusLabel = (job) => {
  if (!job) return "";
  if (job.status === "queued") return "Wartet";
  if (job.status === "running") return job.step || "Läuft";
  if (job.status === "failed") return "Fehlgeschlagen";
  return "Fertig";
};

/** Farbe eines Scores gegen das Limit: grün = bestanden, gelb = knapp darunter, rot = deutlich darunter */
export const scoreColor = (score, limit = 85) => {
  if (score === null || score === undefined) return "#9e9aa8";
  if (score >= limit) return "#1f9d61";
  if (score >= limit - 15) return "#d99a0b";
  return "#d8493f";
};

/** Farbverschiebung { r, g, b } als „R +3,1 G −0,4 B −2,0" */
export const formatColorShift = (shift) => {
  if (!shift) return "";
  const f = (v) => `${v > 0 ? "+" : v < 0 ? "−" : "±"}${Math.abs(Number(v) || 0).toFixed(1)}`;
  return `R ${f(shift.r)} G ${f(shift.g)} B ${f(shift.b)}`;
};

export const formatUsd = (value) => (value === null || value === undefined ? "–" : `$${Number(value).toFixed(value < 0.1 ? 3 : 2)}`);

export const formatDuration = (ms) => {
  if (!ms && ms !== 0) return "–";
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")} min`;
};

export const jobTime = (job) => {
  const ts = job && job.createdAt;
  const date = ts && ts.toDate ? ts.toDate() : null;
  return date ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
};

export const sourceLabel = (source) => ({ order: "Bestellung", lab: "Lab", test: "Test" }[source] || source || "–");

/**
 * Regionen eines Jobs für Anzeige und Editor: Analyse (vorher) + Detail-Pass (nachher)
 * zusammengeführt. `box` in Promille des Bildes, `rect` = tatsächlich überarbeiteter
 * Bereich in Pixeln (nur wenn die Region neu gerendert und eingesetzt wurde).
 */
export function jobRegions(job) {
  const analysis = (job && job.report && job.report.analysis) || null;
  const detail = (job && job.report && job.report.detail) || null;
  if (!analysis || !Array.isArray(analysis.regions)) return [];
  const upscale = (job.stages && job.stages.upscale) || {};
  return analysis.regions.map((r) => {
    const d = detail && Array.isArray(detail.regions) ? detail.regions.find((x) => x.id === r.id) : null;
    const replaced = !!(d && d.after && !d.skipped);
    // Wo liegt die erkannte Box im Ausschnitt (0…1)? Die Ausschnitte sind großzügig (Rand +
    // Aufweitung) — für den Gesichtsvergleich wird damit eng auf die Box gezoomt.
    let inner = null;
    if (d && d.rect && r.boxResult && upscale.width && upscale.height) {
      const bx = (r.boxResult.x / 1000) * upscale.width;
      const by = (r.boxResult.y / 1000) * upscale.height;
      const bw = (r.boxResult.width / 1000) * upscale.width;
      const bh = (r.boxResult.height / 1000) * upscale.height;
      inner = { x: (bx - d.rect.left) / d.rect.width, y: (by - d.rect.top) / d.rect.height, w: bw / d.rect.width, h: bh / d.rect.height };
    }
    return {
      id: r.id,
      type: r.type,
      label: r.label,
      box: r.boxResult,
      scoreBefore: r.score,
      scoreAfter: d && d.score !== null && d.score !== undefined ? d.score : null,
      score: d && d.score !== null && d.score !== undefined ? d.score : r.score,
      deviationsBefore: r.deviations || [],
      deviations: d && Array.isArray(d.deviations) && d.deviations.length ? d.deviations : replaced ? [] : r.deviations || [],
      skipped: d ? d.skipped || null : null,
      // Wächter-Warnung des aktiven Renders (manueller Edit, trotzdem eingesetzt) — rotes „!" im Bild und an der Kachel
      warning: d ? d.warning || null : null,
      // Landmarken-Ausrichtung des Renders (Maßstab, Drehung, Rest) — Anzeige an der Regionskarte
      geometry: d ? d.geometry || null : null,
      replaced,
      edited: !!(r.edited || (d && d.edit)),
      // Versionen des Bereichs (Detail-Pass + Region-Edits), aktive Version — regionEdit.ts
      versions: (job.regionVersions && job.regionVersions[r.id] && job.regionVersions[r.id].versions) || [],
      activeVersion: job.regionVersions && job.regionVersions[r.id] ? job.regionVersions[r.id].active : null,
      inner,
      // Seitenverhältnis des Ausschnitts (Breite / Höhe) — für die Darstellung des Zuschnitts
      cropRatio: d && d.rect && d.rect.height ? d.rect.width / d.rect.height : null,
      rect: replaced ? d.rect || null : null,
      // exakte Einsetz-Maske (Graustufen-PNG in Größe von rect) — fehlt bei älteren Jobs
      maskUrl: replaced ? d.mask || null : null,
      crops: { original: r.originalCrop, before: (d && d.before) || r.resultCrop, rendered: d && d.rendered, after: d && d.after, reference: d && d.reference },
      guard: d && d.guard,
      costUsd: d && d.costUsd,
      textOriginal: r.textOriginal,
      textResult: r.textResult,
      scoreNote: r.scoreNote,
    };
  });
}
